import {
  FieldTextarea,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { MetaConversation } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { useState } from 'react';
import * as React from 'react';

import { notificationActions } from 'components/notifications/actions/notification';
import { transferToAgentActions } from 'components/shared/transferToAgentModal/actions/transferToAgentModal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { TransferToAgentItem } from '../../transferToAgentDropdown';

import styles from './confirmTransferModal.module.scss';

type ConfirmTransferModalProps = {
  isOpen: boolean;
  onClose?(): void;
  assignedAgent: User;
  transferToUser: TransferToAgentItem;
  metaConversation: MetaConversation;
};

export const ConfirmTransferModal = ({
  isOpen,
  onClose,
  assignedAgent,
  transferToUser,
  metaConversation,
}: ConfirmTransferModalProps): React.ReactElement => {
  const [message, setMessage] = useState('');
  const dispatch = useAppDispatch();

  const transferConversation = async () => {
    try {
      await bindActionToPromise(
        dispatch,
        transferToAgentActions.transferToAgentBlindRequest,
      )({
        requestToUser: { id: transferToUser.id },
        requestFromAgent: assignedAgent,
        metaConversation: metaConversation,
        message: message,
      });

      ampli.conversationTransferred({
        conversationId: metaConversation.conversation.id ?? 0,
        option: 'agent',
      });
      setMessage('');
    } catch (error) {
      const csid = metaConversation.conversation.id;

      setMessage('');

      dispatch(
        transferToAgentActions.transferToAgentRequestStop({
          csid,
        }),
      );

      dispatch(
        notificationActions.notificationAdd({
          title: 'Could not transfer the conversation',
          text: `An issue occurred while trying transfer conversation ${csid}.`,
          status: 'danger',
          icon: 'exclamation',
        }),
      );

      console.warn(error);
    }
  };

  const handleOnClose = () => {
    setMessage('');
    onClose?.();
  };

  const handleOnMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement> | undefined,
  ) => {
    if (!event) {
      return;
    }

    setMessage(event.target.value);
  };

  const handleOnKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement> | undefined,
  ) => {
    if (!event) {
      return;
    }

    if (event.key === 'Enter' && event.metaKey) {
      handleOnClose();
      transferConversation();
    }
  };

  const handleConfirmOnClick = () => {
    handleOnClose();
    transferConversation();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose}>
      <ModalHead
        title={`Transfer ${metaConversation.conversation.id} to ${transferToUser.name}`}
        onClose={onClose}
      />

      <ModalBody className={styles.messageModalBody}>
        <FieldTextarea
          autoFocus={true}
          input={{
            name: 'message',
            value: message,
            onChange: handleOnMessageChange,
          }}
          label="Transfer message"
          placeholder={`Write a message to ${transferToUser.name}...`}
          onKeyDown={handleOnKeyDown}
        />
      </ModalBody>
      <ModalFoot
        confirmName="Transfer"
        confirmOnClick={handleConfirmOnClick}
        cancelName="Cancel transfer"
        cancelOnClick={handleOnClose}
      />
    </Modal>
  );
};
